/**
 * countries.js — Single source of truth for the country dropdown used everywhere
 * (member companies, contacts, events…). Maps each country to its default language so
 * the platform can dispatch transactional emails & notifications in the right locale.
 *
 * `lang` codes match the platform i18n (fr|en|es|it|pt).
 */

export const COUNTRIES = [
  { code: "FR", name: "France", flag: "🇫🇷", lang: "fr" },
  { code: "BE", name: "Belgique", flag: "🇧🇪", lang: "fr" },
  { code: "CH", name: "Suisse", flag: "🇨🇭", lang: "fr" },
  { code: "LU", name: "Luxembourg", flag: "🇱🇺", lang: "fr" },
  { code: "MC", name: "Monaco", flag: "🇲🇨", lang: "fr" },
  { code: "CA", name: "Canada", flag: "🇨🇦", lang: "fr" },

  { code: "GB", name: "Royaume-Uni", flag: "🇬🇧", lang: "en" },
  { code: "US", name: "États-Unis", flag: "🇺🇸", lang: "en" },
  { code: "IE", name: "Irlande", flag: "🇮🇪", lang: "en" },
  { code: "AU", name: "Australie", flag: "🇦🇺", lang: "en" },
  { code: "NZ", name: "Nouvelle-Zélande", flag: "🇳🇿", lang: "en" },
  { code: "ZA", name: "Afrique du Sud", flag: "🇿🇦", lang: "en" },

  { code: "ES", name: "Espagne", flag: "🇪🇸", lang: "es" },
  { code: "MX", name: "Mexique", flag: "🇲🇽", lang: "es" },
  { code: "AR", name: "Argentine", flag: "🇦🇷", lang: "es" },
  { code: "CL", name: "Chili", flag: "🇨🇱", lang: "es" },
  { code: "CO", name: "Colombie", flag: "🇨🇴", lang: "es" },
  { code: "PE", name: "Pérou", flag: "🇵🇪", lang: "es" },

  { code: "IT", name: "Italie", flag: "🇮🇹", lang: "it" },

  { code: "PT", name: "Portugal", flag: "🇵🇹", lang: "pt" },
  { code: "BR", name: "Brésil", flag: "🇧🇷", lang: "pt" },

  { code: "DE", name: "Allemagne", flag: "🇩🇪", lang: "en" },
  { code: "NL", name: "Pays-Bas", flag: "🇳🇱", lang: "en" },
  { code: "AT", name: "Autriche", flag: "🇦🇹", lang: "en" },
  { code: "DK", name: "Danemark", flag: "🇩🇰", lang: "en" },
  { code: "FI", name: "Finlande", flag: "🇫🇮", lang: "en" },
  { code: "GR", name: "Grèce", flag: "🇬🇷", lang: "en" },
  { code: "HU", name: "Hongrie", flag: "🇭🇺", lang: "en" },
  { code: "PL", name: "Pologne", flag: "🇵🇱", lang: "en" },
  { code: "RO", name: "Roumanie", flag: "🇷🇴", lang: "en" },
  { code: "SE", name: "Suède", flag: "🇸🇪", lang: "en" },
  { code: "NO", name: "Norvège", flag: "🇳🇴", lang: "en" },
  { code: "CZ", name: "République tchèque", flag: "🇨🇿", lang: "en" },
  { code: "BG", name: "Bulgarie", flag: "🇧🇬", lang: "en" },
  { code: "HR", name: "Croatie", flag: "🇭🇷", lang: "en" },
  { code: "SI", name: "Slovénie", flag: "🇸🇮", lang: "en" },
  { code: "SK", name: "Slovaquie", flag: "🇸🇰", lang: "en" },

  { code: "MA", name: "Maroc", flag: "🇲🇦", lang: "fr" },
  { code: "TN", name: "Tunisie", flag: "🇹🇳", lang: "fr" },
  { code: "DZ", name: "Algérie", flag: "🇩🇿", lang: "fr" },
  { code: "SN", name: "Sénégal", flag: "🇸🇳", lang: "fr" },

  { code: "CN", name: "Chine", flag: "🇨🇳", lang: "en" },
  { code: "JP", name: "Japon", flag: "🇯🇵", lang: "en" },
  { code: "KR", name: "Corée du Sud", flag: "🇰🇷", lang: "en" },
  { code: "IN", name: "Inde", flag: "🇮🇳", lang: "en" },
  { code: "SG", name: "Singapour", flag: "🇸🇬", lang: "en" },
];

/** Find a country entry by its display name (case-insensitive). */
export function findCountry(name) {
  if (!name) return null;
  const norm = String(name).trim().toLowerCase();
  return COUNTRIES.find((c) => c.name.toLowerCase() === norm || c.code.toLowerCase() === norm) || null;
}

/** Get the default language for a given country name; defaults to "fr". */
export function langForCountry(name) {
  return findCountry(name)?.lang || "fr";
}

/** Country → flag emoji (returns "" when unknown). */
export function flagFor(name) {
  return findCountry(name)?.flag || "";
}

/** Country → ISO code (returns "" when unknown). */
export function codeFor(name) {
  return findCountry(name)?.code || "";
}
